import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo } from "react";
import {
  ButtonSize,
  Modal,
  Button,
  ButtonVariant,
  IconButton
} from "shared/components";
import { Card } from "shared/components/Card";
import { ApplicantType, Application } from "shared/types";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { XClose } from "untitledui-js-base";
import { cx } from "shared/utils";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { ApplicationStatusBadge } from "../ApplicationStatusBadge";
import { Template } from "types/landlord/verificationTemplate";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicationUIStatus } from "utils/get-application-ui-status";
import { getApplicantRole } from "utils/getApplicantRole";

const getApplication = async (id: string) => {
  return instance(`/portal/v1/applications/${id}`).then(
    ({ data }) => data as Application & { verification_template: Template }
  );
};

export const UncancelModal = ({
  onClose,
  applicationId,
  onSuccess,
  applicantId
}: {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
}) => {
  const navigate = useNavigate();

  const applicationMeta = useQuery({
    queryKey: ["application", String(applicationId)],
    queryFn: () => getApplication(applicationId),
    enabled: Boolean(applicationId)
  });

  const displayStatus = getApplicationUIStatus(applicationMeta.data);

  const allUsers = [
    ...(applicationMeta.data?.applicants || []),
    ...(applicationMeta.data?.pending_magic_links || [])
  ];

  const applicant = allUsers.find(
    (applicant) => applicantId && String(applicant.id) === String(applicantId)
  );

  const tableData = useMemo(() => {
    return (applicant ? [applicant] : allUsers).map((applicant) => ({
      ...applicant,
      paidBy:
        (applicant.role === ApplicantType.CoSigner &&
          applicationMeta.data?.verification_template.co_signer_fee.cents ===
            0) ||
        (applicant.role === ApplicantType.Applicant &&
          applicationMeta.data?.verification_template.applicant_fee.cents === 0)
          ? "Partner"
          : getApplicantRole(applicant.role)
    }));
  }, [applicationMeta.data?.applicants, applicantId, applicant]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!applicantId && applicationId)
        return instance.post(
          `/portal/v1/applications/${applicationId}/uncancel`
        );

      if (!("active" in (applicant || {})))
        return instance.post(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/uncancel`
        );

      return instance.post(`/portal/v1/magic_links/${applicant?.id}/uncancel`);
    },
    mutationKey: ["uncancel", "application", String(applicationId)],
    onSuccess: () => {
      toast.success(`Application successfully uncanceled`);
      if (onSuccess) onSuccess();
      else navigate(`/applications/${applicationId}`);

      onClose();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName="max-w-[600px] max-h-[640px] w-full min-h-[300px] flex flex-col h-full"
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">
          Confirm uncanceling application
        </h1>
        <IconButton
          onClick={onClose}
          borderLess
          className="ml-auto translate-x-2"
          Icon={XClose}
        />
      </div>

      <div className="h-full grow-1 overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              The following application will be uncanceled
            </p>
            <Card
              className={cx("px-6 ", tableData.length > 1 ? "py-4" : "py-0")}
            >
              {tableData.length > 1 ? (
                <div className="flex gap-2 items-center justify-between">
                  <div className="max-w-[380px]">
                    <p className="text-sm text-content-400 mb-2">
                      Application group
                    </p>
                    <h3 className="truncate text-base text-content-1000 font-bold pb-4 border-b border-alpha-40">
                      {tableData
                        .map((applicant) =>
                          "first_name" in applicant &&
                          applicant.first_name &&
                          applicant.last_name
                            ? getFullName({
                                firstName: applicant.first_name,
                                lastName: applicant.last_name,
                                middleName: applicant.middle_name,
                                short: true
                              })
                            : applicant.email
                        )
                        .join(", ")}
                    </h3>
                  </div>

                  <ApplicationStatusBadge
                    className="shrink-0"
                    type="application"
                    status={displayStatus}
                  />
                </div>
              ) : null}

              <ul className="mt-4">
                {tableData.map((applicant) => (
                  <li key={applicant.email} className="mb-4">
                    <p className="text-sm text-content-1000 font-medium mb-2">
                      {"first_name" in applicant &&
                      applicant.first_name &&
                      applicant.last_name
                        ? getFullName({
                            firstName: applicant.first_name,
                            lastName: applicant.last_name,
                            middleName: applicant.middle_name,
                            short: true
                          })
                        : applicant.email}
                    </p>
                    <p className="text-xs text-content-400">
                      {getApplicantRole(applicant.role)}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button size={ButtonSize.SM} onClick={onClose}>
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            mutate();
          }}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending}
        >
          Uncancel application
        </Button>
      </div>
    </Modal>
  );
};
